import React, { FunctionComponent } from "react";

import { AnimatedHeight } from "../AnimatedHeight";
import { AnimatedOpacity } from "../AnimatedOpacity";
import { AnimatedWidth } from "../AnimatedWidth";
import { DelayedUnmount } from "../DelayedUnmount";
import { AnimationPhase } from "../types";

/** Výčet pro typ animace */
export enum AnimationType {
    opacity = "opacity",
    width = "width",
    height = "height",
}

export type AnimatedMountProps = {
    /** Příznak, zda má být potomek zapojený v DOM */
    mounted: boolean;
    /** Typ animace při změně příznaku pro zapojení */
    animationType: AnimationType;
    /** Délka trvání animace při změně příznaku pro zapojení */
    animationDurationMs: number;
    /** Šířka komponenty při animované šířce */
    width?: number;
    /** Výška komponenty při animované výšce */
    height?: number;
};

/**
 * Komponenta slouží pro animované přidávání/odebírání komponent do/z DOM.
 */
export const AnimatedMount: FunctionComponent<AnimatedMountProps> = ({
    mounted,
    animationType,
    children,
    animationDurationMs,
    width,
    height,
}) => {
    const animationPhase = mounted ? AnimationPhase.start : AnimationPhase.end;
    const widthAnimationType = animationType === AnimationType.width;
    const heightAnimationType = animationType === AnimationType.height;

    if (widthAnimationType && width && (heightAnimationType && height) === undefined) {
        throw new Error("[AnimatedMount] Width or Height prop was not provided.");
    }

    const animatedOptions = () => {
        switch (animationType) {
            case AnimationType.width:
                return (
                    <AnimatedWidth width={width || 0} // Zde bude šířka vždy definovaná, typová kontrola to však nerozpozná
                        {...{ animationDurationMs, animationPhase }}
                    >
                        {children}
                    </AnimatedWidth>
                );
            case AnimationType.height:
                return (
                    <AnimatedHeight height={height || 0} // Zde bude výška vždy definovaná, typová kontrola to však nerozpozná
                        {...{ animationDurationMs, animationPhase }}
                    >
                        {children}
                    </AnimatedHeight>
                );
            case AnimationType.opacity:
                return (
                    <AnimatedOpacity {...{ animationDurationMs, animationPhase }}>
                        {children}
                    </AnimatedOpacity>
                );
        }
    };

    return (
        <DelayedUnmount
            delayMs={animationDurationMs}
            {...{ mounted }}
        >
            {animatedOptions()}
        </DelayedUnmount>
    );
};
